/* eslint-disable no-undef */
/* eslint-disable react/no-array-index-key */
import { useEffect } from 'react';
import {
  Box,
  Chip,
  Grid,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { capitalize, isEmpty, isNil, isNull, map, omitBy, startCase, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  listBulkLabelPrintHistoryByPage,
  sortBulkLabelHistoryQueue,
} from 'store/thunks/workQueueThunks';
import { visuallyHidden } from '@mui/utils';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import dayjs from 'dayjs';

const FILTER_KEY_LABELS = {
  ndcs: 'NDCs',
};

const headCells = [
  {
    id: 'createdAt',
    align: 'left',
    sortable: true,
    label: 'Printed On',
  },

  {
    id: 'pomUserName',
    align: 'left',
    sortable: true,
    label: 'User Name',
  },

  {
    id: 'fileKey',
    align: 'left',
    sortable: false,
    label: 'File Key',
  },

  {
    id: 'bulkLabelFilter',
    align: 'left',
    sortable: false,
    label: 'Filters',
  },

  {
    id: 'signedUrl',
    align: 'left',
    sortable: false,
    label: 'Actions',
  },
];

const BulkLabelPrintHistoryTable = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(listBulkLabelPrintHistoryByPage({ page: 1 }));
  }, [dispatch]);

  const { sortBy, pages, currentPage } = useSelector(
    ({ workQueue }) => workQueue?.bulkLabels?.bulkLabelHistory
  );

  const bulkLabelHistory = pages[currentPage];

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.bulkLabels.bulkLabelHistory.count / 25)
  );

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didn't change, don't do anything.
      return;
    }

    dispatch(listBulkLabelPrintHistoryByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortBulkLabelHistoryQueue({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  return (
    <Grid>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align}
                    style={
                      headCell.id === 'fillType' ? { position: 'relative', right: '10px' } : {}
                    }
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        data-head-property={headCell.id}
                        onClick={handleSort}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {map(
                bulkLabelHistory,
                ({ pomUserName, createdTime, signedUrl, fileKey, bulkLabelFilter }) => {
                  return (
                    <TableRow>
                      <TableCell align='left'>
                        {format(new Date(createdTime), 'yyyy-MM-dd HH:mm:ss')}{' '}
                      </TableCell>
                      <TableCell align='left'> {pomUserName} </TableCell>

                      <TableCell align='left'> {fileKey} </TableCell>

                      <TableCell align='left'>
                        {bulkLabelFilter ? (
                          <Grid container spacing={1} sx={{ maxWidth: 500, flexWrap: 'wrap' }}>
                            {map(Object.keys(omitBy(bulkLabelFilter, isNull)), (key) => {
                              const value =
                                key === 'lastPrintedDate'
                                  ? dayjs(bulkLabelFilter[key]).format('YYYY-MM-DD')
                                  : bulkLabelFilter[key];
                              return (
                                <Grid item key={key}>
                                  <Chip
                                    label={`${FILTER_KEY_LABELS[key] || startCase(key)} = ${
                                      Array.isArray(value)
                                        ? value.map(capitalize).join(', ')
                                        : capitalize(value)
                                    }`}
                                    variant='contained'
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>

                      <TableCell>
                        <Link
                          href={signedUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          underline='hover'
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <DownloadIcon fontSize='small' />
                          Download Labels
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>

            {!isNil(bulkLabelHistory) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={14} align='right'>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>
      {isNil(bulkLabelHistory) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(bulkLabelHistory) && isEmpty(bulkLabelHistory) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default BulkLabelPrintHistoryTable;
